import React, { useEffect, useRef, useState } from 'react'
import html2canvas from 'html2canvas'
import { Copy, Download, ExternalLink, Instagram, Save, X } from 'lucide-react'
import { socialStudioService } from '../../services/socialStudioService'
import SocialVisual from './SocialVisual'

const formatPrice = (value) => Number.isFinite(Number(value)) ? `${Math.round(Number(value)).toLocaleString('cs-CZ')} Kč` : '—'
const formatConfidence = (value) => Number.isFinite(Number(value)) ? `${Math.round(Number(value) * 100)} %` : '—'

const EvidenceRow = ({ label, value }) => value === null || value === undefined || value === '' ? null : (
  <div className="flex items-start justify-between gap-4 border-b border-slate-200 py-2.5 last:border-0">
    <span className="text-xs text-slate-500">{label}</span>
    <span className="text-right text-xs font-medium text-slate-900">{value}</span>
  </div>
)

const SocialPostEditor = ({ post, onClose, onSaved, onCopy }) => {
  const [form, setForm] = useState({
    headline: post.draft?.headline || '',
    instagramCaption: post.draft?.instagramCaption || '',
    xText: post.draft?.xText || '',
    altText: post.draft?.altText || '',
    hashtags: (post.draft?.hashtags || []).join(' '),
  })
  const [saving, setSaving] = useState(false)
  const [downloading, setDownloading] = useState(false)
  const [error, setError] = useState('')
  const visualRef = useRef(null)

  useEffect(() => {
    const handleKey = (event) => {
      if (event.key === 'Escape') onClose()
    }
    window.addEventListener('keydown', handleKey)
    return () => window.removeEventListener('keydown', handleKey)
  }, [onClose])

  const previewPost = {
    ...post,
    draft: { ...post.draft, headline: form.headline },
  }

  const handleChange = (field) => (event) => setForm((current) => ({ ...current, [field]: event.target.value }))

  const handleSave = async () => {
    try {
      setSaving(true)
      setError('')
      const updated = await socialStudioService.savePost(post._id, {
        ...form,
        revision: post.draft.revision,
      })
      onSaved(updated)
    } catch (requestError) {
      setError(requestError.message || 'Návrh se nepodařilo uložit.')
    } finally {
      setSaving(false)
    }
  }

  const handleDownload = async () => {
    if (!visualRef.current) return
    try {
      setDownloading(true)
      setError('')
      const canvas = await html2canvas(visualRef.current, {
        backgroundColor: '#0f172a',
        scale: 2.5,
        useCORS: true,
        logging: false,
      })
      const blob = await new Promise((resolve) => canvas.toBlob(resolve, 'image/png', 0.96))
      if (!blob) throw new Error('Obrázek se nepodařilo vytvořit.')
      const url = URL.createObjectURL(blob)
      const anchor = document.createElement('a')
      anchor.href = url
      anchor.download = `realitni-jestrab-${post.slotDate}-${post.slotKey}.png`
      anchor.click()
      window.setTimeout(() => URL.revokeObjectURL(url), 1000)
    } catch (downloadError) {
      console.error('Unable to export social visual:', downloadError)
      setError('Grafiku se nepodařilo stáhnout. Zdroj fotografie pravděpodobně nepovoluje export; můžete otevřít původní fotografii a grafiku uložit ručně.')
    } finally {
      setDownloading(false)
    }
  }

  const listing = post.evidence?.listing
  const market = post.evidence?.market
  const renovated = post.evidence?.valuation?.renovated

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-950/65 p-3 backdrop-blur-sm sm:p-6" role="dialog" aria-modal="true" aria-labelledby="social-editor-title">
      <div className="flex max-h-[96vh] w-full max-w-7xl flex-col overflow-hidden rounded-2xl bg-white shadow-2xl">
        <header className="flex items-center justify-between gap-4 border-b border-slate-200 px-5 py-4 sm:px-6">
          <div>
            <h2 id="social-editor-title" className="text-lg font-semibold text-slate-950">Příprava příspěvku</h2>
            <p className="mt-1 text-sm text-slate-500">{post.scheduledTime} · {post.slotLabel} · nic se automaticky nepublikuje</p>
          </div>
          <button type="button" onClick={onClose} className="rounded-lg p-2 text-slate-500 hover:bg-slate-100" aria-label="Zavřít"><X className="h-5 w-5" /></button>
        </header>

        <div className="grid min-h-0 flex-1 overflow-y-auto xl:grid-cols-[470px_minmax(0,1fr)] xl:overflow-hidden">
          <aside className="space-y-5 border-b border-slate-200 bg-slate-50 p-5 xl:overflow-y-auto xl:border-b-0 xl:border-r sm:p-6">
            <div className="flex justify-center">
              <SocialVisual post={previewPost} ref={visualRef} />
            </div>
            <div className="grid grid-cols-2 gap-2">
              <button type="button" disabled={downloading} onClick={handleDownload} className="inline-flex items-center justify-center gap-2 rounded-xl bg-slate-950 px-4 py-2.5 text-sm font-semibold text-white hover:bg-slate-800 disabled:opacity-50">
                <Download className="h-4 w-4" /> {downloading ? 'Vytvářím…' : 'Stáhnout PNG'}
              </button>
              {listing?.imageUrl ? (
                <a href={listing.imageUrl} target="_blank" rel="noreferrer" className="inline-flex items-center justify-center gap-2 rounded-xl border border-slate-300 px-4 py-2.5 text-sm font-medium text-slate-700 hover:bg-white">
                  Původní foto <ExternalLink className="h-4 w-4" />
                </a>
              ) : <div />}
            </div>

            <section className="rounded-2xl border border-slate-200 bg-white p-4">
              <div className="text-xs font-semibold uppercase tracking-[0.14em] text-violet-700">Ověřený podklad</div>
              <div className="mt-2">
                <EvidenceRow label="Zdroj" value={listing?.title || `Tržní přehled: ${market?.region}`} />
                <EvidenceRow label="Lokalita" value={listing?.location || market?.region} />
                <EvidenceRow label="Nabídková cena" value={listing?.price ? formatPrice(listing.price) : null} />
                <EvidenceRow label="Cena za m²" value={listing?.m2Price ? `${formatPrice(listing.m2Price)}/m²` : market?.medianPricePerM2 ? `${formatPrice(market.medianPricePerM2)}/m² medián` : null} />
                <EvidenceRow label="Odhad po rekonstrukci" value={renovated?.anchorPrice ? formatPrice(renovated.anchorPrice) : null} />
                <EvidenceRow label="Důvěra odhadu" value={renovated?.confidence ? formatConfidence(renovated.confidence) : null} />
                <EvidenceRow label="Aktivní nabídky" value={market?.activeListingCount?.toLocaleString('cs-CZ')} />
              </div>
            </section>

          </aside>

          <main className="space-y-5 p-5 xl:overflow-y-auto sm:p-6">
            {error && <div className="rounded-xl border border-red-200 bg-red-50 p-3 text-sm text-red-800">{error}</div>}

            <div>
              <label htmlFor="social-headline" className="text-sm font-medium text-slate-700">Titulek grafiky</label>
              <input id="social-headline" value={form.headline} maxLength={100} onChange={handleChange('headline')} className="mt-2 w-full rounded-xl border border-slate-300 px-3 py-2.5 text-sm outline-none focus:border-violet-500 focus:ring-2 focus:ring-violet-100" />
              <div className="mt-1 text-right text-xs text-slate-400">{form.headline.length}/100</div>
            </div>

            <div>
              <div className="flex items-center justify-between gap-3">
                <label htmlFor="social-instagram" className="inline-flex items-center gap-2 text-sm font-medium text-slate-700"><Instagram className="h-4 w-4" /> Instagram</label>
                <button type="button" onClick={() => onCopy(`${form.instagramCaption}\n\n${form.hashtags}`, 'Instagram text zkopírován')} className="inline-flex items-center gap-1.5 text-xs font-medium text-violet-700"><Copy className="h-3.5 w-3.5" /> Kopírovat</button>
              </div>
              <textarea id="social-instagram" rows={9} value={form.instagramCaption} maxLength={2200} onChange={handleChange('instagramCaption')} className="mt-2 w-full resize-y rounded-xl border border-slate-300 px-3 py-3 text-sm leading-6 outline-none focus:border-violet-500 focus:ring-2 focus:ring-violet-100" />
              <div className="mt-1 text-right text-xs text-slate-400">{form.instagramCaption.length}/2200</div>
            </div>

            <div>
              <div className="flex items-center justify-between gap-3">
                <label htmlFor="social-x-text" className="text-sm font-medium text-slate-700">X / vlákno</label>
                <button type="button" onClick={() => onCopy(form.xText, 'Text pro X zkopírován')} className="inline-flex items-center gap-1.5 text-xs font-medium text-violet-700"><Copy className="h-3.5 w-3.5" /> Kopírovat</button>
              </div>
              <textarea id="social-x-text" rows={6} value={form.xText} maxLength={1000} onChange={handleChange('xText')} className="mt-2 w-full resize-y rounded-xl border border-slate-300 px-3 py-3 text-sm leading-6 outline-none focus:border-violet-500 focus:ring-2 focus:ring-violet-100" />
              <div className="mt-1 text-right text-xs text-slate-400">{form.xText.length}/1000</div>
            </div>

            <div className="grid gap-4 sm:grid-cols-2">
              <div>
                <label htmlFor="social-hashtags" className="text-sm font-medium text-slate-700">Hashtagy</label>
                <textarea id="social-hashtags" rows={3} value={form.hashtags} onChange={handleChange('hashtags')} className="mt-2 w-full rounded-xl border border-slate-300 px-3 py-3 text-sm outline-none focus:border-violet-500 focus:ring-2 focus:ring-violet-100" />
              </div>
              <div>
                <label htmlFor="social-alt" className="text-sm font-medium text-slate-700">Alternativní text</label>
                <textarea id="social-alt" rows={3} value={form.altText} maxLength={500} onChange={handleChange('altText')} className="mt-2 w-full rounded-xl border border-slate-300 px-3 py-3 text-sm outline-none focus:border-violet-500 focus:ring-2 focus:ring-violet-100" />
              </div>
            </div>

            <div className="flex items-center justify-end gap-3 border-t border-slate-200 pt-5">
              <button type="button" onClick={onClose} className="rounded-xl border border-slate-300 px-4 py-2.5 text-sm font-medium text-slate-700 hover:bg-slate-50">Zrušit</button>
              <button type="button" disabled={saving} onClick={handleSave} className="inline-flex items-center gap-2 rounded-xl bg-violet-700 px-4 py-2.5 text-sm font-semibold text-white hover:bg-violet-800 disabled:opacity-50">
                <Save className="h-4 w-4" /> {saving ? 'Ukládám…' : 'Uložit návrh'}
              </button>
            </div>
          </main>
        </div>
      </div>
    </div>
  )
}

export default SocialPostEditor
